/**
 * Framework-agnostic web font loader for DP2 template text.
 *
 * Why this exists: browsers load `@font-face` fonts lazily and drawing to a canvas never triggers
 * the load, so a Konva preview silently falls back to a default font unless something calls
 * `document.fonts.load()` first. This module registers every face from the server's manifest
 * (one `FontFace` per legacy family/weight/style), loads the ones a template needs on demand,
 * dedupes in-flight work, and notifies subscribers when faces land so a preview can re-measure.
 *
 * No Vue imports here on purpose: the store preloaders, MLivePreview (via `useFontLoader`) and
 * the future template design tool all share this one registry.
 */
import { fetchFontManifest } from './fontManifest.service';
import {
  DP2_TSTYLE,
  type EnsureFontsOptions,
  type EnsureFontsResult,
  type FontManifest,
  type FontManifestFace,
  type KonvaFontStyle,
  type ResolvedFont,
} from './types';

export type ManifestFetcher = () => Promise<FontManifest>;

type FaceStatus = 'unloaded' | 'loading' | 'loaded' | 'error';

interface RegisteredFace {
  key: string;
  face: FontManifestFace;
  fontFace: FontFace | null;
  status: FaceStatus;
  loadPromise: Promise<void> | null;
}

const GENERIC_FAMILIES = new Set(['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui']);
export const FALLBACK_FAMILY = 'sans-serif';

const registry = new Map<string, RegisteredFace>();
const familyIndex = new Map<string, RegisteredFace[]>();
const listeners = new Set<() => void>();

let manifestVersion: string | null = null;
let manifestPromise: Promise<FontManifest> | null = null;
let fetcher: ManifestFetcher = fetchFontManifest;
let notifyScheduled = false;
/** After a failed manifest fetch, callers get an immediate rejection for this long instead of a new request each. */
let manifestRetryDelayMs = 30_000;
let manifestFailedAt = 0;

const normalizeFamily = (family: string | null | undefined): string => (family ?? '').trim().toLowerCase();

const faceKey = (face: FontManifestFace): string => `${normalizeFamily(face.family)}|${face.weight}|${face.style}`;

/** The two members we use; older TS DOM libs type `FontFaceSet` without them. */
interface FontFaceSetLike {
  add(face: FontFace): unknown;
  delete(face: FontFace): boolean;
}

function getFontSet(): FontFaceSetLike | undefined {
  return typeof document !== 'undefined' ? (document as Document & { fonts?: FontFaceSetLike }).fonts : undefined;
}

function canConstructFontFace(): boolean {
  return typeof FontFace !== 'undefined';
}

/** Coalesces several faces landing in the same tick into one notification. */
function notify(): void {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => {
    notifyScheduled = false;
    for (const listener of Array.from(listeners)) {
      try {
        listener();
      } catch (e) {
        console.warn('[fontLoader] listener threw', e);
      }
    }
  });
}

/** Subscribe to "some font finished loading". Returns an unsubscribe function. */
export function onFontsChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Swap the manifest source (tests, or a design tool pointing at a draft manifest) or the failure back-off. */
export function configureFontLoader(options: { fetcher?: ManifestFetcher; manifestRetryDelayMs?: number }): void {
  if (options.fetcher) fetcher = options.fetcher;
  if (options.manifestRetryDelayMs !== undefined) manifestRetryDelayMs = options.manifestRetryDelayMs;
}

/**
 * Register the manifest's faces with the browser. Idempotent per manifest version: a repeat call
 * with the same version is a no-op, a new version adds/replaces changed faces and drops removed ones.
 * Registering does not download anything; that happens in `ensureFonts`.
 */
export function injectManifest(manifest: FontManifest): void {
  if (!manifest || manifest.version === manifestVersion) return;

  const fontSet = getFontSet();
  const incomingKeys = new Set<string>();

  for (const manifestFace of manifest.faces ?? []) {
    if (!manifestFace?.family || !manifestFace.url) continue;

    // One FontFace per name the templates might use. Aliases share the URL, and the browser's HTTP
    // cache (the file route is immutable) means the bytes are only fetched once.
    const names = [manifestFace.family, ...(manifestFace.aliases ?? [])]
      .map((n) => (n ?? '').trim())
      .filter((n) => n.length > 0);
    for (const name of new Set(names.map((n) => n.toLowerCase()))) {
      const face: FontManifestFace = { ...manifestFace, family: names.find((n) => n.toLowerCase() === name)! };
      registerFace(face, fontSet, incomingKeys);
    }
  }

  for (const [key, registered] of Array.from(registry.entries())) {
    if (incomingKeys.has(key)) continue;
    if (registered.fontFace && fontSet) {
      try {
        fontSet.delete(registered.fontFace);
      } catch {
        /* already gone */
      }
    }
    registry.delete(key);
  }

  familyIndex.clear();
  for (const registered of registry.values()) {
    const family = normalizeFamily(registered.face.family);
    const list = familyIndex.get(family) ?? [];
    list.push(registered);
    familyIndex.set(family, list);
  }

  manifestVersion = manifest.version;
}

function registerFace(face: FontManifestFace, fontSet: FontFaceSetLike | undefined, incomingKeys: Set<string>): void {
  const key = faceKey(face);
  incomingKeys.add(key);

  const existing = registry.get(key);
  if (existing && existing.face.url === face.url) return;

  if (existing?.fontFace && fontSet) {
    try {
      fontSet.delete(existing.fontFace);
    } catch {
      /* already gone */
    }
  }

  let fontFace: FontFace | null = null;
  if (canConstructFontFace()) {
    try {
      fontFace = new FontFace(face.family.trim(), `url(${face.url})`, {
        weight: String(face.weight),
        style: face.style || 'normal',
      });
      fontSet?.add(fontFace);
    } catch (e) {
      console.warn('[fontLoader] could not register face', face, e);
      fontFace = null;
    }
  }

  registry.set(key, { key, face, fontFace, status: 'unloaded', loadPromise: null });
}

/** Fetch (once) and inject the manifest. Concurrent callers share the in-flight request. */
export function ensureManifest(): Promise<FontManifest> {
  if (manifestPromise) return manifestPromise;

  // Thirty previews on one page must not each retry a failing endpoint; back off briefly.
  if (manifestFailedAt && Date.now() - manifestFailedAt < manifestRetryDelayMs) {
    return Promise.reject(new Error('font manifest unavailable (backing off)'));
  }

  manifestPromise = fetcher()
    .then((manifest) => {
      manifestFailedAt = 0;
      injectManifest(manifest);
      return manifest;
    })
    .catch((error) => {
      // Let a later caller retry (after the back-off) instead of caching the failure forever.
      manifestPromise = null;
      manifestFailedAt = Date.now();
      throw error;
    });

  return manifestPromise;
}

/**
 * Drop the cached manifest and fetch it again (after an admin upload/delete/toggle). Faces whose
 * URL did not change stay registered and loaded; changed ones are re-registered.
 */
export function refreshManifest(): Promise<FontManifest> {
  manifestPromise = null;
  manifestVersion = null;
  return ensureManifest();
}

export function isManifestLoaded(): boolean {
  return manifestVersion !== null;
}

export function getManifestVersion(): string | null {
  return manifestVersion;
}

/** True when the (already injected) manifest has at least one face for this family. */
export function isServable(family: string | null | undefined): boolean {
  const normalized = normalizeFamily(family);
  return normalized.length > 0 && familyIndex.has(normalized);
}

function loadFace(registered: RegisteredFace): Promise<void> {
  if (registered.loadPromise) return registered.loadPromise;

  if (!registered.fontFace) {
    // No FontFace API (tests / SSR): nothing to load, treat as loaded so callers do not wait.
    registered.status = 'loaded';
    registered.loadPromise = Promise.resolve();
    return registered.loadPromise;
  }

  registered.status = 'loading';
  registered.loadPromise = registered.fontFace
    .load()
    .then(() => {
      registered.status = 'loaded';
      // TODO(fonts-logging): temporary diagnostics, remove once preloading is verified in the field.
      console.info(
        `[fonts] face loaded: "${registered.face.family}" ${registered.face.weight} ${registered.face.style} <- ${registered.face.url}`,
      );
      notify();
    })
    .catch((error) => {
      registered.status = 'error';
      console.warn('[fontLoader] failed to load', registered.face.family, error);
      notify();
    });

  return registered.loadPromise;
}

/**
 * Make sure every face of the given families is loaded (or known to be missing/failed).
 * Never rejects. With `timeoutMs`, resolves early with `timedOut: true` while the loads continue;
 * `onFontsChanged` fires as they land, which is how a preview re-measures late fonts.
 */
export async function ensureFonts(
  families: Iterable<string>,
  options: EnsureFontsOptions = {},
): Promise<EnsureFontsResult> {
  const wanted = Array.from(
    new Set(
      Array.from(families)
        .map((f) => (f ?? '').trim())
        .filter((f) => f.length > 0),
    ),
  );
  const result: EnsureFontsResult = {
    loaded: [],
    alreadyLoaded: [],
    missing: [],
    failed: [],
    timedOut: false,
    manifestUnavailable: false,
  };

  if (wanted.length === 0) return result;

  // The manifest fetch shares the caller's time budget: a slow endpoint must not stall every
  // preview on the page past the timeout. If it lands later, the requested families are loaded
  // then and subscribers are notified as usual.
  const { timeoutMs } = options;
  const deadline =
    timeoutMs && timeoutMs > 0
      ? new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), timeoutMs))
      : null;

  const manifestReady = ensureManifest().then(
    () => 'ok' as const,
    (error) => {
      console.warn('[fontLoader] font manifest unavailable', error);
      return 'error' as const;
    },
  );
  const manifestOutcome = deadline ? await Promise.race([manifestReady, deadline]) : await manifestReady;

  if (manifestOutcome === 'error') {
    result.manifestUnavailable = true;
    result.missing = wanted;
    return result;
  }
  if (manifestOutcome === 'timeout') {
    void manifestReady.then((outcome) => {
      if (outcome === 'ok') void ensureFonts(wanted);
    });
    result.timedOut = true;
    result.missing = wanted;
    return result;
  }

  const perFamily = new Map<string, RegisteredFace[]>();
  const pending: Promise<void>[] = [];

  for (const family of wanted) {
    const faces = familyIndex.get(normalizeFamily(family));
    if (!faces || faces.length === 0) {
      result.missing.push(family);
      continue;
    }
    perFamily.set(family, faces);
    if (faces.every((f) => f.status === 'loaded')) result.alreadyLoaded.push(family);
    for (const face of faces) pending.push(loadFace(face));
  }

  if (pending.length > 0) {
    const settled = Promise.allSettled(pending).then(() => false as const);
    if (deadline) {
      result.timedOut = (await Promise.race([settled, deadline.then(() => true as const)])) === true;
    } else {
      await settled;
    }
  }

  for (const [family, faces] of perFamily) {
    if (faces.some((f) => f.status === 'error')) result.failed.push(family);
    else if (faces.every((f) => f.status === 'loaded')) result.loaded.push(family);
  }

  return result;
}

const parseTstyle = (tstyle: string | number | null | undefined): number => {
  if (typeof tstyle === 'number') return Number.isFinite(tstyle) ? tstyle : 0;
  const parsed = parseInt((tstyle ?? '').toString().trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Map a DP2 text node's `Tfont` (Windows legacy family name) and `Tstyle` bitmask to what Konva
 * and canvas measurement need. `Tfont` maps straight to the CSS family because the manifest
 * registers faces under the same legacy names; Bold on a weight-only family (e.g. "Barlow
 * SemiBold") therefore gets browser-synthesized bold, which is exactly what GDI does in DP2.
 */
export function resolveFont(
  tfont: string | null | undefined,
  tstyle: string | number | null | undefined,
): ResolvedFont {
  const family = (tfont ?? '').trim() || FALLBACK_FAMILY;
  const bits = parseTstyle(tstyle);
  const bold = (bits & DP2_TSTYLE.BOLD) !== 0;
  const italic = (bits & DP2_TSTYLE.ITALIC) !== 0;
  const underline = (bits & DP2_TSTYLE.UNDERLINE) !== 0;

  const fontStyle: KonvaFontStyle = italic && bold ? 'italic bold' : bold ? 'bold' : italic ? 'italic' : 'normal';

  return {
    fontFamily: family,
    fontStyle,
    textDecoration: underline ? 'underline' : '',
    bold,
    italic,
    underline,
    isServable: isServable(family),
  };
}

export const DEFAULT_RESOLVED_FONT: ResolvedFont = Object.freeze(resolveFont(undefined, undefined));

/** CSS font shorthand for `CanvasRenderingContext2D.font`, with the family quoted when it needs to be. */
export function toCanvasFont(font: ResolvedFont, fontSizePx: number): string {
  const family = font.fontFamily.trim();
  const quoted = GENERIC_FAMILIES.has(family.toLowerCase()) ? family : `"${family.replace(/"/g, '')}"`;
  const style = font.italic ? 'italic ' : '';
  const weight = font.bold ? 'bold ' : '';
  return `${style}${weight}${fontSizePx}px ${quoted}`;
}

/** Distinct `Tfont` values from a parsed DP2 template's nodes (any object with `properties`). */
export function collectTemplateFonts(
  nodes: Record<string, { properties?: Record<string, string> }> | null | undefined,
): string[] {
  if (!nodes) return [];
  const fonts = new Set<string>();
  for (const node of Object.values(nodes)) {
    const font = node?.properties?.['Tfont']?.trim();
    if (font) fonts.add(font);
  }
  return Array.from(fonts);
}

/** Test hook: forget the manifest, registry and listeners. */
export function __resetFontLoaderForTests(): void {
  const fontSet = getFontSet();
  for (const registered of registry.values()) {
    if (registered.fontFace && fontSet) {
      try {
        fontSet.delete(registered.fontFace);
      } catch {
        /* ignore */
      }
    }
  }
  registry.clear();
  familyIndex.clear();
  listeners.clear();
  manifestVersion = null;
  manifestPromise = null;
  fetcher = fetchFontManifest;
  notifyScheduled = false;
  manifestRetryDelayMs = 30_000;
  manifestFailedAt = 0;
}
